import { useCallback, useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import { HEADER_TOKEN_KEYS, sanitizeHeaderHtml } from "@vet/print-core";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

type Props = { value: string; onChange: (html: string) => void };

const TOKEN_CLASS = "rounded bg-accent px-1.5 text-accent-foreground";

export function HeaderTextEditor({ value, onChange }: Props) {
  const { t } = useTranslation("settings");
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const lastEmitted = useRef<string | null>(null);

  const tokenLabel = useCallback((key: string) => t(`printEditor.header.tokens.${key}`), [t]);

  const decorate = useCallback(
    (html: string) => {
      const box = document.createElement("div");
      box.innerHTML = sanitizeHeaderHtml(html);
      box.querySelectorAll<HTMLElement>("span[data-token]").forEach((span) => {
        span.textContent = tokenLabel(span.dataset.token ?? "");
        span.contentEditable = "false";
        span.className = TOKEN_CLASS;
      });
      return box.innerHTML;
    },
    [tokenLabel],
  );

  const serialize = useCallback(() => {
    const el = ref.current;
    if (!el) return "";
    const clone = el.cloneNode(true) as HTMLElement;
    clone.querySelectorAll<HTMLElement>("span[data-token]").forEach((span) => {
      const key = span.dataset.token ?? "";
      const style = span.getAttribute("style");
      while (span.attributes.length) span.removeAttribute(span.attributes[0].name);
      span.setAttribute("data-token", key);
      if (style) span.setAttribute("style", style);
      span.textContent = "";
    });
    return sanitizeHeaderHtml(clone.innerHTML);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || value === lastEmitted.current) return;
    el.innerHTML = decorate(value);
    lastEmitted.current = value;
  }, [value, decorate]);

  const emit = useCallback(() => {
    const html = serialize();
    lastEmitted.current = html;
    onChange(html);
  }, [serialize, onChange]);

  const focusEditor = () => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (sel && (sel.rangeCount === 0 || !el.contains(sel.anchorNode))) {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  const insertToken = (key: string) => {
    focusEditor();
    const html = `<span data-token="${key}" contenteditable="false" class="${TOKEN_CLASS}">${tokenLabel(key)}</span>&nbsp;`;
    document.execCommand("insertHTML", false, html);
    emit();
  };

  return (
    <div className="flex flex-col gap-2">
      <Label id={`${id}-label`} htmlFor={`${id}-editor`}>
        {t("printEditor.header.text")}
      </Label>
      <div className="overflow-hidden rounded-xl border bg-card focus-within:ring-2 focus-within:ring-ring">
        <div
          id={`${id}-editor`}
          ref={ref}
          role="textbox"
          aria-multiline="true"
          aria-labelledby={`${id}-label`}
          contentEditable
          suppressContentEditableWarning
          onInput={emit}
          onBlur={emit}
          className={cn("min-h-[132px] px-3 py-3 text-sm leading-snug focus:outline-none")}
        />
      </div>
      <div role="group" aria-label={t("printEditor.header.insertField")} className="flex flex-wrap gap-1.5">
        {HEADER_TOKEN_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insertToken(key)}
            className="rounded-full border bg-card px-2.5 py-1 text-xs hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            + {tokenLabel(key)}
          </button>
        ))}
      </div>
    </div>
  );
}
